import {
	ChevronDownIcon,
	ChevronRightIcon,
	Cog6ToothIcon,
	CodeBracketIcon,
	PencilSquareIcon,
	PlusIcon,
	TrashIcon,
	EyeIcon
} from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { EyeSvg } from '../../../../components/Icons'
import { getReplyOptions } from '../utils'
import { CARD_TYPE, CAROUSEL_TYPE, RBM_ELEMENT_ERRORS, REPLY_BUTTON, TEXT_TYPE } from './constants'
import CreateModal from './CreateModal'
import FlowBuilder from './FlowBuilder'
import { FlowDiagram } from './FlowDiagram'
import ButtonForm from './forms/ButtonForm'
import FlowConfigPanel from './FlowConfigPanel'
import InteractiveDemo from './InteractiveDemo'
import NewElementForm from './NewElementForm'
import PhonePreview from './PhonePreview'
import TemplateJsonViewer from './TemplateJsonViewer'

const VIEW_MODES = {
  DIAGRAM: 'diagram',
  CLASSIC: 'classic'
}

const EDITOR_MODES = {
  EDIT: 'edit',
  JSON: 'json'
}

const getRbmElements = (rbmElements) => {
  const rbmElementsCopy = { ...rbmElements }
  delete rbmElementsCopy.rbmFlowAliasesMap
  const groupedElements = {}
  Object.keys(rbmElementsCopy)
    .map((key) => key.split('-')[0])
    .forEach((flow) => {
      if (!groupedElements[flow]) {
        groupedElements[flow] = []
        const flowLength = Object.keys(rbmElementsCopy).filter(
          (key) => key.split('-')[0] === flow
        ).length
        for (let i = 0; i < flowLength; i++) {
          groupedElements[flow].push(
            rbmElementsCopy[`${flow}-${i + 1}/${flowLength}`]
          )
        }
      }
    })
  return groupedElements
}

const getRbmFlowAliasesMap = (rbmElements) => {
  if (rbmElements.rbmFlowAliasesMap) {
    return rbmElements.rbmFlowAliasesMap
  }
  return {}
}

const ConversationalFlow = ({
  rbmElements = {},
  setMobileMessages,
  setRbmElements,
  emailFields = [],
  campaignName = '',
  templateElements = null,
  fullTemplate = null,
  onJsonSave = null,
  onFlowConfigChange = null
}) => {
  const [rbmFlows, setRbmFlows] = useState(
    Object.keys(rbmElements).length > 0
      ? getRbmElements(rbmElements)
      : { init: [] }
  )
  const [visibleFlow, setVisibleFlow] = useState('init')
  const [rbmFlowAliasesMap, setAliasesState] = useState(
    getRbmFlowAliasesMap(rbmElements)
  )

  // View mode
  const [viewMode, setViewMode] = useState(VIEW_MODES.DIAGRAM)
  const [expandedFlows, setExpandedFlows] = useState({ init: true })
  const [editorMode, setEditorMode] = useState(EDITOR_MODES.EDIT)

  // Modal states
  const [elementModalOpen, setElementModalOpen] = useState(false)
  const [buttonModalOpen, setButtonModalOpen] = useState(false)
  const [editingFlowId, setEditingFlowId] = useState(null)
  const [editingElementIndex, setEditingElementIndex] = useState(null)
  const [editingButtonIndex, setEditingButtonIndex] = useState(null)
  const [errors, setErrors] = useState([])

  useEffect(() => {
    setMobileMessages(rbmFlows[visibleFlow])
  }, [])

  // Update rbmFlows when rbmElements changes (e.g., when loading an existing template)
  useEffect(() => {
    if (Object.keys(rbmElements).length > 0) {
      const newFlows = getRbmElements(rbmElements)
      setRbmFlows(newFlows)
      setAliasesState(getRbmFlowAliasesMap(rbmElements))
    }
  }, [rbmElements])

  useEffect(() => {
    if (!rbmFlows[visibleFlow]) {
      setVisibleFlow('init')
      return
    }
    setMobileMessages(rbmFlows[visibleFlow])
  }, [rbmFlows])

  useEffect(() => {
    setMobileMessages(rbmFlows[visibleFlow])
  }, [visibleFlow])

  const setRbmFlowAliasesMap = (newRbmFlowAliasesMap) => {
    setAliasesState(newRbmFlowAliasesMap)
    setRbmElements({
      ...rbmElements,
      rbmFlowAliasesMap: newRbmFlowAliasesMap
    })
  }

  const updateRbmFlows = (newRbmFlows) => {
    setRbmFlows(newRbmFlows)
    const newRbmElements = Object.keys(newRbmFlows).reduce((acc, flow) => {
      newRbmFlows[flow].forEach((element, index) => {
        acc[`${flow}-${index + 1}/${newRbmFlows[flow].length}`] = element
      })
      return acc
    }, {})
    newRbmElements.rbmFlowAliasesMap = rbmFlowAliasesMap
    setRbmElements(newRbmElements)
  }

  const getFlowAlias = (flowId) => {
    if (rbmFlowAliasesMap[flowId]) {
      return rbmFlowAliasesMap[flowId]
    }
    if (flowId.includes('response')) {
      return 'Response ' + flowId.split('response')[1]
    }
    return flowId
  }

  const isAliasInUse = (alias, flowId) => {
    const rbmFlowAliases = Object.keys(rbmFlows)
      .map((flow) => {
        if (flow !== flowId && flow !== 'init') return getFlowAlias(flow)
        return null
      })
      .filter((alias) => alias !== null)
    return rbmFlowAliases.includes(alias)
  }

  const addFlow = () => {
    let num = Object.keys(rbmFlows).length
    let newFlowId = 'response' + num
    while (
      rbmFlows[newFlowId] ||
      isAliasInUse(getFlowAlias(newFlowId), newFlowId)
    ) {
      newFlowId = 'response' + ++num
    }
    const newRbmFlows = { ...rbmFlows, [newFlowId]: [] }
    updateRbmFlows(newRbmFlows)
  }

  const isResponseInUse = (flowId) => {
    const isReplyInButtons = (buttons, flowId) => {
      return buttons.some((button) => {
        if (button.type === REPLY_BUTTON && button.replyFlow === flowId) {
          return true
        }
        return false
      })
    }
    return Object.keys(rbmFlows).some((flow) => {
      if (flow === flowId) return false
      const flowElements = rbmFlows[flow]
      if (!flowElements || !Array.isArray(flowElements)) return false
      return flowElements.some((element) => {
        if (!element) return false // Skip undefined/null elements
        let replyInFlow = false
        if (element.suggestions) {
          replyInFlow = isReplyInButtons(element.suggestions, flowId)
          if (replyInFlow) return true
        }
        switch (element.type) {
          case CARD_TYPE:
            if (element.buttons) {
              replyInFlow = isReplyInButtons(element.buttons, flowId)
              if (replyInFlow) return true
            }
            break
          case CAROUSEL_TYPE:
            if (element.cards) {
              replyInFlow = element.cards.some((card) => {
                if (card.buttons) {
                  return isReplyInButtons(card.buttons, flowId)
                }
                return false
              })
              if (replyInFlow) return true
            }
            break
          default:
            break
        }
        return false
      })
    })
  }

  const deleteFlow = (flowId) => {
    if (isResponseInUse(flowId)) {
      alert('This response is being used for a reply. Please remove the reference before deleting it.')
      return
    }
    const newRbmFlows = { ...rbmFlows }
    delete newRbmFlows[flowId]
    
    const newRbmElements = Object.keys(newRbmFlows).reduce((acc, flow) => {
      newRbmFlows[flow].forEach((element, index) => {
        acc[`${flow}-${index + 1}/${newRbmFlows[flow].length}`] = element
      })
      return acc
    }, {})
    
    const newRbmFlowAliasesMap = { ...rbmFlowAliasesMap }
    delete newRbmFlowAliasesMap[flowId]
    
    setRbmFlows(newRbmFlows)
    setRbmElements({
      ...newRbmElements,
      rbmFlowAliasesMap: newRbmFlowAliasesMap
    })
  }

  // Element editing
  const validateElement = (element) => {
    const newErrors = []
    if (!element.name) {
      newErrors.push(RBM_ELEMENT_ERRORS.EMPTY_NAME)
    }
    switch (element.type) {
      case TEXT_TYPE:
        if (!element.text) {
          newErrors.push(RBM_ELEMENT_ERRORS.EMPTY_TEXT)
        }
        break
      case CARD_TYPE:
        if (!element.image || !(element.image.uploaded || element.image.file)) {
          newErrors.push(RBM_ELEMENT_ERRORS.MISSING_IMAGE)
        }
        break
      case CAROUSEL_TYPE:
        if (!element.cards || element.cards.length === 0) {
          newErrors.push(RBM_ELEMENT_ERRORS.MISSING_CARDS)
        }
        break
      default:
        break
    }
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleOpenAddElement = (flowId) => {
    setEditingFlowId(flowId)
    setEditingElementIndex(null)
    setErrors([])
    setElementModalOpen(true)
  }

  const handleOpenEditElement = (flowId, elementIndex) => {
    setEditingFlowId(flowId)
    setEditingElementIndex(elementIndex)
    setErrors([])
    setElementModalOpen(true)
  }

  const handleSaveElement = (elementInfo) => {
    if (!validateElement(elementInfo)) return

    const flowElements = [...(rbmFlows[editingFlowId] || [])]
    
    if (editingElementIndex !== null) {
      // Update existing
      // Preserve suggestions if it's the last element
      if (editingElementIndex === flowElements.length - 1 && flowElements[editingElementIndex]?.suggestions) {
        elementInfo.suggestions = flowElements[editingElementIndex].suggestions
      }
      flowElements[editingElementIndex] = elementInfo
    } else {
      // Add new
      // Move suggestions from previous last element to new last element
      if (flowElements.length > 0 && flowElements[flowElements.length - 1].suggestions) {
        elementInfo.suggestions = flowElements[flowElements.length - 1].suggestions
        delete flowElements[flowElements.length - 1].suggestions
      }
      flowElements.push(elementInfo)
    }

    const newRbmFlows = { ...rbmFlows, [editingFlowId]: flowElements }
    updateRbmFlows(newRbmFlows)
    setElementModalOpen(false)
    setEditingFlowId(null)
    setEditingElementIndex(null)
  }

  const handleDeleteElement = (flowId, elementIndex) => {
    const flowElements = [...(rbmFlows[flowId] || [])]
    if (flowElements.length === 0) return

    // If deleting the last element, move suggestions to the new last element
    if (elementIndex === flowElements.length - 1 && flowElements[elementIndex]?.suggestions) {
      if (flowElements.length > 1) {
        flowElements[flowElements.length - 2].suggestions = flowElements[elementIndex].suggestions
      }
    }

    flowElements.splice(elementIndex, 1)
    const newRbmFlows = { ...rbmFlows, [flowId]: flowElements }
    updateRbmFlows(newRbmFlows)
  }

  // Button/Suggestion editing
  const handleOpenAddButton = (flowId) => {
    setEditingFlowId(flowId)
    setEditingButtonIndex(null)
    setButtonModalOpen(true)
  }

  const handleOpenEditButton = (flowId, buttonIndex) => {
    setEditingFlowId(flowId)
    setEditingButtonIndex(buttonIndex)
    setButtonModalOpen(true)
  }

  const getSuggestionsFromFlow = (flowId) => {
    const flowElements = rbmFlows[flowId] || []
    if (flowElements.length === 0) return []
    const lastElement = flowElements[flowElements.length - 1]
    return lastElement.suggestions || []
  }

  const handleSaveButton = (buttonInfo) => {
    const flowElements = [...(rbmFlows[editingFlowId] || [])]
    if (flowElements.length === 0) return

    const lastElementIndex = flowElements.length - 1
    const lastElement = { ...flowElements[lastElementIndex] }
    const suggestions = [...(lastElement.suggestions || [])]

    if (editingButtonIndex !== null) {
      suggestions[editingButtonIndex] = buttonInfo
    } else {
      suggestions.push(buttonInfo)
    }

    lastElement.suggestions = suggestions
    flowElements[lastElementIndex] = lastElement

    const newRbmFlows = { ...rbmFlows, [editingFlowId]: flowElements }
    updateRbmFlows(newRbmFlows)
    setButtonModalOpen(false)
    setEditingFlowId(null)
    setEditingButtonIndex(null)
  }

  const getEditingElement = () => {
    if (!editingFlowId || editingElementIndex === null) return {}
    return rbmFlows[editingFlowId]?.[editingElementIndex] || {}
  }

  const getEditingButton = () => {
    if (!editingFlowId || editingButtonIndex === null) return undefined
    const suggestions = getSuggestionsFromFlow(editingFlowId)
    return suggestions[editingButtonIndex]
  }

  // Classic view functions
  const setFlow = (flowId, newFlow) => {
    const newRbmFlows = { ...rbmFlows, [flowId]: newFlow }
    updateRbmFlows(newRbmFlows)
  }

  const getRbmFlowElements = (flowId) => {
    const rbmFlowElements = {}
    const flowArray = rbmFlows[flowId] || []
    for (let i = 0; i < flowArray.length; i++) {
      rbmFlowElements[`${flowId}-${i + 1}/${flowArray.length}`] = flowArray[i]
    }
    return rbmFlowElements
  }

  const toggleFlowExpanded = (flowId) => {
    setExpandedFlows((prev) => ({
      ...prev,
      [flowId]: !prev[flowId]
    }))
  }

  const onClickEditFlowName = (flowAliasRef) => {
    flowAliasRef.current.focus()
  }

  const handleKeyDownOnFlowAlias = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.blur()
      const selection = window.getSelection()
      selection.removeAllRanges()
    }
  }

  const handleAliasChange = (e, flowId) => {
    if (e.currentTarget.innerText.trim() === '') {
      e.currentTarget.innerText = getFlowAlias(flowId)
    }
    if (isAliasInUse(e.currentTarget.innerText.trim(), flowId)) {
      e.currentTarget.innerText = getFlowAlias(flowId)
      alert('This flow name already exists. Please choose another one.')
      return
    }
    e.currentTarget.innerText = e.currentTarget.innerText.trim()
    setRbmFlowAliasesMap({
      ...rbmFlowAliasesMap,
      [flowId]: e.currentTarget.innerText.trim()
    })
  }

  const handleRenameFlow = (flowId, newName) => {
    if (!newName || newName.trim() === '') {
      return
    }
    const trimmedName = newName.trim()
    if (isAliasInUse(trimmedName, flowId)) {
      alert('This flow name already exists. Please choose another one.')
      return
    }
    setRbmFlowAliasesMap({
      ...rbmFlowAliasesMap,
      [flowId]: trimmedName
    })
  }

  const renderClassicFlowEditor = (flowId, isInit = false) => {
    const flowAliasRef = React.createRef()
    const isExpanded = expandedFlows[flowId] !== false

    return (
      <div
        key={flowId}
        className={`flex flex-col ${!isInit ? 'mt-4 pt-4 border-t-2 border-blue-light/50' : ''}`}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleFlowExpanded(flowId)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-grey-blue" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-grey-blue" />
            )}
          </button>

          {isInit ? (
            <h1 className="text-xl font-bold text-grey-blue">
              Initial Communication
            </h1>
          ) : (
            <>
              <h1
                ref={flowAliasRef}
                contentEditable={true}
                suppressContentEditableWarning={true}
                className="text-xl font-bold text-grey-blue focus:outline-none focus:border-b-2 focus:border-grey-blue"
                onBlur={(e) => handleAliasChange(e, flowId)}
                onKeyDown={(e) => handleKeyDownOnFlowAlias(e)}
                spellCheck={false}
              >
                {getFlowAlias(flowId)}
              </h1>
              <button
                className="border-none outline-none"
                onClick={() => onClickEditFlowName(flowAliasRef)}
              >
                <PencilSquareIcon className="h-5 w-5 text-grey-blue" />
              </button>
            </>
          )}

          <button
            onClick={() => setVisibleFlow(flowId)}
            className={`ml-2 p-1 rounded ${
              visibleFlow === flowId
                ? 'text-indigo-600 bg-indigo-50'
                : 'text-grey-blue/50 hover:text-grey-blue'
            } transition-colors`}
            title="Preview this flow"
          >
            <EyeSvg className="h-5 w-5 stroke-current" />
          </button>
        </div>

        {isExpanded && (
          <>
            <FlowBuilder
              flowId={flowId}
              emailFields={emailFields}
              rbmElements={rbmFlows[flowId] ? getRbmFlowElements(flowId) : {}}
              setFlow={setFlow}
              setMobileMessages={setMobileMessages}
              isConversational
              rbmFlowAliases={rbmFlowAliasesMap}
              rbmFlows={rbmFlows}
            />
            {!isInit && (
              <button
                onClick={() => deleteFlow(flowId)}
                className="h-10 flex w-fit items-center gap-2 border-none outline-none group mt-2"
              >
                <TrashIcon className="h-5 w-5 text-red-300 group-hover:text-red-500 transition-colors" />
                <span className="text-sm text-red-300 font-medium group-hover:text-red-500 transition-colors">
                  Delete Response
                </span>
              </button>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Element Modal - for new diagram view */}
      <CreateModal open={elementModalOpen} setOpen={setElementModalOpen}>
        <NewElementForm
          key={`element-${editingFlowId}-${editingElementIndex}`}
          onClick={handleSaveElement}
          elementInfo={getEditingElement()}
          setOpen={setElementModalOpen}
          isConversational={true}
          replyOptions={getReplyOptions(rbmFlows, rbmFlowAliasesMap, editingFlowId)}
          errors={errors}
          emailFields={emailFields}
        />
      </CreateModal>

      {/* Button Modal - for new diagram view */}
      <CreateModal open={buttonModalOpen} setOpen={setButtonModalOpen} isSmall={true}>
        <ButtonForm
          key={`button-${editingFlowId}-${editingButtonIndex}`}
          onClick={handleSaveButton}
          buttonInfo={getEditingButton()}
          setOpen={setButtonModalOpen}
          onCancel={() => {
            setButtonModalOpen(false)
            setEditingFlowId(null)
            setEditingButtonIndex(null)
          }}
          isConversational={true}
          replyOptions={getReplyOptions(rbmFlows, rbmFlowAliasesMap, editingFlowId)}
          emailFields={emailFields}
        />
      </CreateModal>

      {/* Editor Mode Toggle */}
      <div className="mb-4 flex items-center gap-2">
        <div className="inline-flex bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setEditorMode(EDITOR_MODES.EDIT)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              editorMode === EDITOR_MODES.EDIT
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <PencilSquareIcon className="h-4 w-4" />
            Edición
          </button>
          <button
            onClick={() => setEditorMode(EDITOR_MODES.JSON)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              editorMode === EDITOR_MODES.JSON
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CodeBracketIcon className="h-4 w-4" />
            JSON
          </button>
        </div>
      </div>

      {editorMode === EDITOR_MODES.JSON ? (
        /* JSON View */
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <TemplateJsonViewer
              template={fullTemplate || rbmElements}
              campaignName={campaignName}
              onSave={onJsonSave}
            />
          </div>
          <div className="sticky top-0 self-start h-[calc(100vh-120px)] flex-shrink-0 py-2">
            <InteractiveDemo
              rbmFlows={rbmFlows}
              rbmFlowAliases={rbmFlowAliasesMap}
            />
          </div>
        </div>
      ) : viewMode === VIEW_MODES.DIAGRAM ? (
        /* New Diagram View */
        <div className="flex gap-6">
          {/* Flow Diagram - Left side (scrollable) */}
          <div className="flex-1 min-w-0">
            {/* Flow Configuration Panel - shows expectedFlow/formFlow */}
            {templateElements && (
              <FlowConfigPanel
                elements={templateElements}
                campaignName={campaignName}
                expectedFlow={fullTemplate?.expectedFlow}
                formFlow={fullTemplate?.formFlow}
                onUpdateElements={onFlowConfigChange}
              />
            )}
            <FlowDiagram
              rbmFlows={rbmFlows}
              rbmFlowAliases={rbmFlowAliasesMap}
              selectedFlow={visibleFlow}
              onSelectFlow={setVisibleFlow}
              onEditElement={handleOpenEditElement}
              onAddElement={handleOpenAddElement}
              onDeleteElement={handleDeleteElement}
              onEditButton={handleOpenEditButton}
              onAddButton={handleOpenAddButton}
              onAddFlow={addFlow}
              onDeleteFlow={deleteFlow}
              onRenameFlow={handleRenameFlow}
              onSwitchView={() => setViewMode(VIEW_MODES.CLASSIC)}
            />
          </div>

          {/* Interactive Demo - Right side (sticky) */}
          <div className="sticky top-0 self-start h-[calc(100vh-120px)] flex-shrink-0 py-2">
            <InteractiveDemo
              rbmFlows={rbmFlows}
              rbmFlowAliases={rbmFlowAliasesMap}
            />
          </div>
        </div>
      ) : (
        /* Classic Editor View */
        <div className="flex gap-4">
          <div className="flex-1">
            {renderClassicFlowEditor('init', true)}

            {Object.keys(rbmFlows).map((flow) => {
              if (flow !== 'init' && flow !== 'rbmFlowAliasesMap') {
                return renderClassicFlowEditor(flow, false)
              }
              return null
            })}

            <button
              onClick={addFlow}
              className="h-12 flex items-center gap-2 border-none outline-none group mt-4"
            >
              <PlusIcon className="h-6 w-6 text-indigo-300 group-hover:text-indigo-500 transition-colors" />
              <span className="text-lg text-indigo-300 font-medium group-hover:text-indigo-500 transition-colors">
                Add Response
              </span>
            </button>

            {/* Switch to Flow Diagram - subtle link */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setViewMode(VIEW_MODES.DIAGRAM)}
                className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Cog6ToothIcon className="h-3.5 w-3.5" />
                Switch to Flow Diagram
              </button>
            </div>
          </div>

          {/* Phone Preview - for classic view */}
          <div className="flex-shrink-0 sticky top-10 h-fit">
            <PhonePreview rbmElements={rbmFlows[visibleFlow] || []} />
          </div>
        </div>
      )}
    </div>
  )
}

export default ConversationalFlow
